import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import {
  AdminEventProductRow,
} from "../interfaces/AdminEventProduct";

const SHEET_NAME = "상품";

function normalizeCell(v: unknown): string {
  if (v === null || v === undefined) return "";
  if (typeof v === "boolean") return v ? "TRUE" : "FALSE";
  return String(v).trim();
}

/** 표시유무 열: 대소문자 무관 `Y` / `N` 만 허용 */
function parseDisplayYn(v: unknown): boolean {
  const s = normalizeCell(v).toUpperCase();
  if (s === "Y") return true;
  if (s === "N") return false;
  if (!s) return false;
  throw new Error(
    `표시유무는 Y 또는 N만 허용합니다. (입력: ${String(v)})`,
  );
}

/** 한글 헤더만 허용 */
function cell(obj: Record<string, unknown>, header: string): string {
  const direct = obj[header];
  if (direct !== undefined && direct !== "") return normalizeCell(direct);
  for (const [k, v] of Object.entries(obj)) {
    if (normalizeCell(k) === header) {
      return normalizeCell(v);
    }
  }
  return "";
}

function apiRowToExcelRow(r: AdminEventProductRow): Record<string, string> {
  return {
    상품코드: r.productCode,
    위치: r.location,
    메모: r.note,
    상품명: r.product,
    체험여부: r.demoInfo,
    표시유무: r.isSearchable ? "Y" : "N",
  };
}

export function downloadEventProductsExcel(
  rows: AdminEventProductRow[],
  fileBaseName: string,
): void {
  const sheetRows = rows.map(apiRowToExcelRow);
  const worksheet = XLSX.utils.json_to_sheet(
    sheetRows.length > 0 ? sheetRows : [apiRowToExcelRow(emptyRow())],
  );
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, SHEET_NAME);
  const buf = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
  const blob = new Blob([buf], { type: "application/octet-stream" });
  const day = new Date().toISOString().split("T")[0];
  saveAs(blob, `${fileBaseName}_${day}.xlsx`);
}

function emptyRow(): AdminEventProductRow {
  return {
    productCode: "",
    location: "",
    note: "",
    product: "",
    demoInfo: "",
    isSearchable: false,
  };
}

export async function parseEventProductsExcel(
  file: File,
): Promise<AdminEventProductRow[]> {
  const buf = await file.arrayBuffer();
  const workbook = XLSX.read(buf, { type: "array" });
  const sheet =
    workbook.Sheets[SHEET_NAME] ?? workbook.Sheets[workbook.SheetNames[0] ?? ""];
  if (!sheet) {
    throw new Error("시트를 찾을 수 없습니다.");
  }
  const raw = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, {
    defval: "",
    raw: false,
  });
  const rows: AdminEventProductRow[] = [];
  for (const obj of raw) {
    const productCode = cell(obj, "상품코드");
    const location = cell(obj, "위치");
    const note = cell(obj, "메모");
    const product = cell(obj, "상품명");
    const demoInfo = cell(obj, "체험여부");
    const isSearchable = parseDisplayYn(cell(obj, "표시유무"));

    if (
      !productCode &&
      !product &&
      !location &&
      !note &&
      !demoInfo
    ) {
      continue;
    }
    rows.push({
      productCode,
      location,
      note,
      product,
      demoInfo,
      isSearchable,
    });
  }
  return rows;
}
